"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import { useContentTab } from "@/components/brain/ContentTabContext";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import MyStreamWaveTabsMemeSubmit from "./MyStreamWaveTabsMemeSubmit";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleLeftIcon, ArrowLeftIcon, MagnifyingGlassIcon, ShareIcon, LinkIcon, CheckIcon } from "@heroicons/react/24/outline";
import WavePicture from "../../../waves/WavePicture";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { createBreakpoint } from "react-use";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
import WaveDescriptionPopover from "@/components/waves/header/WaveDescriptionPopover";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
import MyStreamActionTooltip from "../MyStreamActionTooltip";
import MyStreamWaveCreateCurationAction from "./MyStreamWaveCreateCurationAction";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  MD: 768,
  S: 0
});
interface MyStreamWaveTabsMemeProps {
  readonly wave: ApiWave;
  readonly activeCurationId: string | null;
  readonly onSelectCuration: (curationId: string | null) => void;
}
const MyStreamWaveTabsMeme: React.FC<MyStreamWaveTabsMemeProps> = t0 => {
  const $ = _c(103);
  const {
    wave,
    activeCurationId,
    onSelectCuration
  } = t0;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const [isMemesModalOpen, setIsMemesModalOpen] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const breakpoint = useBreakpoint();
  const isCompact = breakpoint === "S";
  const showExternalCreateCurationAction = breakpoint === "LG";
  const showBackButton = breakpoint !== "LG";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const showShareAction = !isDirectMessage;
  let t1;
  if ($[0] !== wave) {
    t1 = getWaveDescriptionPreviewText(wave);
    $[0] = wave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewText = t1;
  const showDescriptionPreview = showShareAction && !!previewText;
  let t2;
  if ($[2] !== isDirectMessage || $[3] !== wave.id || $[4] !== wave.name) {
    t2 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[2] = isDirectMessage;
    $[3] = wave.id;
    $[4] = wave.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t2);
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  const headerActionsTooltipId = `my-stream-wave-meme-header-actions-${wave.id}`;
  const rightSidebarActionLabel = isRightSidebarOpen ? "Hide right sidebar" : "Show right sidebar";
  let t3;
  if ($[6] !== waveLinkActionFeedbackState || $[7] !== waveLinkActionMode) {
    t3 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[6] = waveLinkActionFeedbackState;
    $[7] = waveLinkActionMode;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const renderWaveLinkActionIcon = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsMemesModalOpen(true);
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleMemesSubmit = t4;
  let t5;
  if ($[10] !== isApp || $[11] !== router || $[12] !== searchParams || $[13] !== wave.chat.scope.group?.is_direct_message) {
    t5 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      params.delete("curation");
      const basePath = getWaveHomeRoute({
        isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[10] = isApp;
    $[11] = router;
    $[12] = searchParams;
    $[13] = wave.chat.scope.group?.is_direct_message;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleMobileBack = t5;
  let t6;
  if ($[15] !== onSelectCuration || $[16] !== pathname || $[17] !== router || $[18] !== searchParams || $[19] !== setActiveContentTab || $[20] !== wave.id || $[21] !== waveChatScroll) {
    t6 = serialNo => {
      onSelectCuration(null);
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.delete("curation");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[15] = onSelectCuration;
    $[16] = pathname;
    $[17] = router;
    $[18] = searchParams;
    $[19] = setActiveContentTab;
    $[20] = wave.id;
    $[21] = waveChatScroll;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const handleSearchSelect = t6;
  let t7;
  if ($[23] !== handleMobileBack || $[24] !== showBackButton) {
    t7 = showBackButton && <button onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[23] = handleMobileBack;
    $[24] = showBackButton;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const t8 = wave.name;
  const t9 = wave.picture;
  let t10;
  if ($[26] !== wave.contributors_overview) {
    t10 = wave.contributors_overview.map(_temp);
    $[26] = wave.contributors_overview;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== wave.name || $[30] !== wave.picture) {
    t11 = <div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={t8} picture={t9} contributors={t10} /></div>;
    $[28] = t10;
    $[29] = wave.name;
    $[30] = wave.picture;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== previewText || $[33] !== showDescriptionPreview || $[34] !== wave) {
    t12 = showDescriptionPreview ? <WaveDescriptionPopover wave={wave} align="left" ariaLabel="Show wave description" triggerClassName="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><h1 className="tw-mb-0 tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1><span className="tw-mt-0.5 tw-block tw-w-full tw-truncate tw-text-xs tw-font-normal tw-text-iron-400">{previewText}</span></WaveDescriptionPopover> : <h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>;
    $[32] = previewText;
    $[33] = showDescriptionPreview;
    $[34] = wave;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t7) {
    t13 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-3">{t7}{t11}{t12}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t7;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== isCompact || $[41] !== wave) {
    t14 = !isCompact && <MyStreamWaveTabsMemeSubmit handleMemesSubmit={handleMemesSubmit} wave={wave} />;
    $[40] = isCompact;
    $[41] = wave;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== handleWaveLinkActionClick || $[44] !== headerActionsTooltipId || $[45] !== renderWaveLinkActionIcon || $[46] !== showShareAction || $[47] !== waveLinkActionIconColor || $[48] !== waveLinkActionLabel || $[49] !== waveLinkActionMode) {
    t15 = showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[43] = handleWaveLinkActionClick;
    $[44] = headerActionsTooltipId;
    $[45] = renderWaveLinkActionIcon;
    $[46] = showShareAction;
    $[47] = waveLinkActionIconColor;
    $[48] = waveLinkActionLabel;
    $[49] = waveLinkActionMode;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsSearchOpen(true);
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== headerActionsTooltipId) {
    t18 = <button type="button" onClick={t16} aria-label="Search messages in this wave" data-tooltip-id={headerActionsTooltipId} data-tooltip-content="Search messages in this wave" className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{t17}</button>;
    $[53] = headerActionsTooltipId;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  const t19 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t20;
  if ($[55] !== t19) {
    t20 = <ChevronDoubleLeftIcon strokeWidth={2} className={t19} />;
    $[55] = t19;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== headerActionsTooltipId || $[58] !== rightSidebarActionLabel || $[59] !== t20 || $[60] !== toggleRightSidebar) {
    t21 = <button type="button" onClick={toggleRightSidebar} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={rightSidebarActionLabel} className="tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-700 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]" aria-label={rightSidebarActionLabel}>{t20}</button>;
    $[57] = headerActionsTooltipId;
    $[58] = rightSidebarActionLabel;
    $[59] = t20;
    $[60] = toggleRightSidebar;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== t14 || $[63] !== t15 || $[64] !== t18 || $[65] !== t21) {
    t22 = <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2 md:tw-shrink-0">{t14}{t15}{t18}{t21}</div>;
    $[62] = t14;
    $[63] = t15;
    $[64] = t18;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== t13 || $[68] !== t22) {
    t23 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-overflow-x-hidden tw-px-2 tw-py-3 sm:tw-px-4">{t13}{t22}</div>;
    $[67] = t13;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== headerActionsTooltipId) {
    t24 = <MyStreamActionTooltip id={headerActionsTooltipId} />;
    $[70] = headerActionsTooltipId;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  const t25 = !showExternalCreateCurationAction;
  let t26;
  if ($[72] !== activeContentTab || $[73] !== activeCurationId || $[74] !== onSelectCuration || $[75] !== setActiveContentTab || $[76] !== t25 || $[77] !== wave) {
    t26 = <div className="tw-min-w-0 tw-flex-1"><MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} showCreateCurationAction={t25} /></div>;
    $[72] = activeContentTab;
    $[73] = activeCurationId;
    $[74] = onSelectCuration;
    $[75] = setActiveContentTab;
    $[76] = t25;
    $[77] = wave;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== onSelectCuration || $[80] !== showExternalCreateCurationAction || $[81] !== wave) {
    t27 = showExternalCreateCurationAction && <MyStreamWaveCreateCurationAction wave={wave} onCreated={onSelectCuration} />;
    $[79] = onSelectCuration;
    $[80] = showExternalCreateCurationAction;
    $[81] = wave;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== t26 || $[84] !== t27) {
    t28 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-x-0 tw-border-y tw-border-solid tw-border-iron-800">{t26}{t27}</div>;
    $[83] = t26;
    $[84] = t27;
    $[85] = t28;
  } else {
    t28 = $[85];
  }
  let t29;
  if ($[86] !== t23 || $[87] !== t24 || $[88] !== t28) {
    t29 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t23}{t24}{t28}</div>;
    $[86] = t23;
    $[87] = t24;
    $[88] = t28;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => setIsMemesModalOpen(false);
    $[90] = t30;
  } else {
    t30 = $[90];
  }
  let t31;
  if ($[91] !== isMemesModalOpen || $[92] !== wave) {
    t31 = <MemesArtSubmissionModal isOpen={isMemesModalOpen} wave={wave} onClose={t30} />;
    $[91] = isMemesModalOpen;
    $[92] = wave;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  let t32;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setIsSearchOpen(false);
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  let t33;
  if ($[95] !== handleSearchSelect || $[96] !== isSearchOpen || $[97] !== wave) {
    t33 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t32} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[95] = handleSearchSelect;
    $[96] = isSearchOpen;
    $[97] = wave;
    $[98] = t33;
  } else {
    t33 = $[98];
  }
  let t34;
  if ($[99] !== t29 || $[100] !== t31 || $[101] !== t33) {
    t34 = <>{t29}{t31}{t33}</>;
    $[99] = t29;
    $[100] = t31;
    $[101] = t33;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  return t34;
};
export default MyStreamWaveTabsMeme;
function _temp(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}